/**
 * Teacher Controls Panel
 * A draggable panel providing teacher/admin controls for managing the game.
 *
 * Features:
 * - Give chips to all players or individual players
 * - Show/hide player cards (all or individual)
 * - Show/hide empty seats
 * - Show/hide current bets
 * - Add bots to the game
 */

import { View, Text, StyleSheet, Pressable, TouchableOpacity } from 'react-native'
import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { Palette } from '@ppwr/poker'
import { ALL_SKILL_LEVELS, type BotSkillLevel } from '@ppwr/poker-engine/utils'
import { Draggable } from '../Draggable/Draggable'
import { useGameQuery } from '../../../hooks/convex/useGameState'
import { useGameContext } from '../../../screens/GameContainer/GameContext'
import { useGamePanelsStore } from '../../../screens/GameContainer/useGamePanelsStore'
import {
  AddBotsSection,
  AddBotsModal,
  GiveChipsSection,
  CardVisibilitySection,
  RemovePlayerSection,
  type PlayerInfo,
  type CardVisibilityMode,
  type BotSkillCounts
} from '../TeacherControls'

const CREAM = Palette['Brand/Cream']
const BLACK = Palette['Global/Black']

interface TeacherControlsPanelProps {
  game: ReturnType<typeof useGameQuery>['game']
  gameId: Id<'games'>
}

function createEmptyBotSkillCounts(): BotSkillCounts {
  return {
    novice: 0,
    casual: 0,
    regular: 0,
    expert: 0
  }
}

export function TeacherControlsPanel({ game, gameId }: TeacherControlsPanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [showChipMenu, setShowChipMenu] = useState(false)
  const [showCardVisibilityMenu, setShowCardVisibilityMenu] = useState(false)
  const [showAddBotsSheet, setShowAddBotsSheet] = useState(false)
  const [isAddingBots, setIsAddingBots] = useState(false)
  const [showRemovePlayerMenu, setShowRemovePlayerMenu] = useState(false)
  const [botSkillCounts, setBotSkillCounts] = useState<BotSkillCounts>(createEmptyBotSkillCounts)
  const { userId } = useGameContext()
  const { setTeacherControls } = useGamePanelsStore()

  // Mutations
  const setCardVisibilityMode = useMutation(api.games.mutations.setCardVisibilityMode)
  const toggleSeatsVisibility = useMutation(api.games.mutations.toggleSeatsVisibility)
  const toggleCurrentBetsVisibility = useMutation(api.games.mutations.toggleCurrentBetsVisibility)
  const addPlayerToCardVisibility = useMutation(api.games.mutations.addPlayerToCardVisibility)
  const removePlayerFromCardVisibility = useMutation(api.games.mutations.removePlayerFromCardVisibility)
  const giveChipsToAllPlayers = useMutation(api.games.mutations.giveChipsToAllPlayers)
  const giveChipsToPlayer = useMutation(api.games.mutations.giveChipsToPlayer)
  const addBotsToGame = useMutation(api.games.mutations.addBotsToGame)
  const pauseGame = useMutation(api.games.mutations.pauseGame)
  const resumeGame = useMutation(api.games.mutations.resumeGame)
  const removePlayerMutation = useMutation(api.games.mutations.removePlayer)

  // Card visibility state
  const cardVisibilityMode = (game?.cardVisibilityMode?.mode ?? 'default') as CardVisibilityMode
  const visiblePlayerUserIds: string[] =
    game?.cardVisibilityMode?.mode === 'byPlayer'
      ? (game.cardVisibilityMode as { mode: 'byPlayer'; userIds: string[] }).userIds
      : []

  // Visibility toggles
  const isShowingEmptySeats = game?.showEmptySeats ?? false
  const isShowingCurrentBets = game?.showCurrentBets ?? true

  const isPaused = game?.state === 'playing' ? Boolean(game.pausedAt) : false

  // Get players
  const players = useQuery(api.players.queries.getPlayers, { gameId })

  // Calculate available seats for add bots feature
  const maxPlayers = game?.maxPlayers ?? 9
  const currentPlayerCount = players?.length ?? 0
  const availableSeats = maxPlayers - currentPlayerCount
  const canAddBots = (game?.state === 'pregame' || game?.state === 'playing') && availableSeats > 0
  const isAddingDuringGame = game?.state === 'playing'

  // Handle give chips to all players
  const handleGiveChipsToAll = useCallback(
    async (amountInK: number) => {
      const amount = amountInK * 1000
      try {
        await giveChipsToAllPlayers({ gameId, amount, userId })
      } catch (error) {
        console.error('[TeacherControlsPanel] Failed to give chips to all:', error)
      }
    },
    [gameId, userId, giveChipsToAllPlayers]
  )

  // Handle give chips to individual player
  const handleGiveChipsToPlayer = useCallback(
    async (targetPlayerId: Id<'players'>, amountInK: number) => {
      const amount = amountInK * 1000
      try {
        await giveChipsToPlayer({ gameId, targetPlayerId, amount, userId })
      } catch (error) {
        console.error('[TeacherControlsPanel] Failed to give chips to player:', error)
      }
    },
    [gameId, userId, giveChipsToPlayer]
  )

  // Prepare player list
  const playerList: PlayerInfo[] = useMemo(() => {
    return (players ?? []).map((p) => ({
      _id: p._id,
      userId: p.userId,
      name: p.name,
      kind: p.kind,
      chips: p.chips ?? 0
    }))
  }, [players])

  // Players that can be removed (exclude current user to avoid removing self)
  const removablePlayers = useMemo(() => playerList.filter((p) => p.userId !== userId), [playerList, userId])

  // Handle card visibility - show all (public: everyone sees all cards)
  const handleShowAllPublic = useCallback(async () => {
    try {
      if (cardVisibilityMode !== 'allPublic') {
        await setCardVisibilityMode({ gameId, userId, mode: 'allPublic' })
      }
    } catch (error) {
      console.error('[TeacherControlsPanel] Failed to show all cards (public):', error)
    }
  }, [gameId, userId, setCardVisibilityMode, cardVisibilityMode])

  // Handle card visibility - show all (private: only the game's teachers see all cards)
  const handleShowAllPrivate = useCallback(async () => {
    try {
      if (cardVisibilityMode !== 'allPrivate') {
        await setCardVisibilityMode({ gameId, userId, mode: 'allPrivate', teacherUserId: userId })
      }
    } catch (error) {
      console.error('[TeacherControlsPanel] Failed to show all cards (private):', error)
    }
  }, [gameId, userId, setCardVisibilityMode, cardVisibilityMode])

  // Handle card visibility - hide all
  const handleHideAllCards = useCallback(async () => {
    try {
      if (cardVisibilityMode !== 'default') {
        await setCardVisibilityMode({ gameId, userId, mode: 'default' })
      }
    } catch (error) {
      console.error('[TeacherControlsPanel] Failed to hide all cards:', error)
    }
  }, [gameId, userId, setCardVisibilityMode, cardVisibilityMode])

  // Handle card visibility - toggle individual player
  const handleTogglePlayerCardVisibility = useCallback(
    async (targetPlayerUserId: string, currentlyVisible: boolean) => {
      try {
        if (currentlyVisible) {
          await removePlayerFromCardVisibility({ gameId, targetPlayerUserId, userId })
        } else {
          await addPlayerToCardVisibility({ gameId, targetPlayerUserId, userId })
        }
      } catch (error) {
        console.error('[TeacherControlsPanel] Failed to toggle player card visibility:', error)
      }
    },
    [gameId, userId, addPlayerToCardVisibility, removePlayerFromCardVisibility]
  )

  // Handle add bots
  const handleCloseAddBotsSheet = useCallback(() => {
    setShowAddBotsSheet(false)
    setBotSkillCounts(createEmptyBotSkillCounts())
  }, [])

  const handleIncrementBotSkill = useCallback(
    (skillLevel: BotSkillLevel) => {
      setBotSkillCounts((currentCounts) => {
        const totalSelected = ALL_SKILL_LEVELS.reduce((sum, level) => sum + currentCounts[level], 0)
        if (totalSelected >= availableSeats) {
          return currentCounts
        }

        return {
          ...currentCounts,
          [skillLevel]: currentCounts[skillLevel] + 1
        }
      })
    },
    [availableSeats]
  )

  const handleDecrementBotSkill = useCallback((skillLevel: BotSkillLevel) => {
    setBotSkillCounts((currentCounts) => ({
      ...currentCounts,
      [skillLevel]: Math.max(0, currentCounts[skillLevel] - 1)
    }))
  }, [])

  const handleAddBots = useCallback(async () => {
    try {
      setIsAddingBots(true)
      await addBotsToGame({ gameId, skillCounts: botSkillCounts, userId })
      handleCloseAddBotsSheet()
    } catch (error) {
      console.error('[TeacherControlsPanel] Failed to add bots:', error)
    } finally {
      setIsAddingBots(false)
    }
  }, [addBotsToGame, botSkillCounts, gameId, handleCloseAddBotsSheet, userId])

  // Handle remove player (works for both humans and bots)
  const handleRemovePlayer = useCallback(
    async (player: PlayerInfo) => {
      try {
        await removePlayerMutation({ gameId, targetUserId: player.userId, userId })
      } catch (error) {
        console.error('[TeacherControlsPanel] Failed to remove player:', error)
      }
    },
    [gameId, removePlayerMutation, userId]
  )

  return (
    <>
      <Draggable minWidth={240} showHandle={true} containerStyle={styles.draggableContainer}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Teacher Controls</Text>
            <View style={styles.headerActions}>
              <TouchableOpacity hitSlop={12} onPress={() => setIsCollapsed(!isCollapsed)}>
                <Text style={styles.collapseIcon}>{isCollapsed ? '▼' : '▲'}</Text>
              </TouchableOpacity>
              <TouchableOpacity hitSlop={12} onPress={() => setTeacherControls(false)}>
                <Text style={styles.closeIcon}>✕</Text>
              </TouchableOpacity>
            </View>
          </View>

          {!isCollapsed && (
            <View style={styles.content}>
              {/* Add Bots Section */}
              {canAddBots && (
                <AddBotsSection
                  availableSeats={availableSeats}
                  isAddingDuringGame={isAddingDuringGame}
                  onOpen={() => setShowAddBotsSheet(true)}
                />
              )}

              {/* Give Chips Section */}
              <GiveChipsSection
                isOpen={showChipMenu}
                onClose={() => setShowChipMenu(false)}
                onOpen={() => setShowChipMenu(true)}
                players={playerList}
                onGiveChipsToAll={handleGiveChipsToAll}
                onGiveChipsToPlayer={handleGiveChipsToPlayer}
              />

              {/* Remove Player Section */}
              {removablePlayers.length > 0 && (
                <RemovePlayerSection
                  isOpen={showRemovePlayerMenu}
                  onClose={() => setShowRemovePlayerMenu(false)}
                  onOpen={() => setShowRemovePlayerMenu(true)}
                  players={removablePlayers}
                  onRemovePlayer={handleRemovePlayer}
                />
              )}

              {/* Show/Hide Empty Seats Button */}
              <Pressable
                onPress={() => toggleSeatsVisibility({ gameId, userId })}
                style={({ pressed }) => [
                  styles.button,
                  isShowingEmptySeats && styles.activeButton,
                  pressed && { opacity: 0.7 }
                ]}
              >
                <Text style={[styles.buttonText, isShowingEmptySeats && styles.activeButtonText]}>
                  {isShowingEmptySeats ? 'Hide Seats' : 'Show Seats'}
                </Text>
              </Pressable>

              {/* Show/Hide Current Bets Button */}
              <Pressable
                onPress={() => toggleCurrentBetsVisibility({ gameId, userId })}
                style={({ pressed }) => [
                  styles.button,
                  isShowingCurrentBets && styles.activeButton,
                  pressed && { opacity: 0.7 }
                ]}
              >
                <Text style={[styles.buttonText, isShowingCurrentBets && styles.activeButtonText]}>
                  {isShowingCurrentBets ? 'Hide Current Bets' : 'Show Current Bets'}
                </Text>
              </Pressable>

              {/* Show/Hide Cards Section */}
              <CardVisibilitySection
                isOpen={showCardVisibilityMenu}
                onClose={() => setShowCardVisibilityMenu(false)}
                onOpen={() => setShowCardVisibilityMenu(true)}
                players={playerList}
                currentMode={cardVisibilityMode}
                visiblePlayerUserIds={visiblePlayerUserIds}
                onShowAllPublic={handleShowAllPublic}
                onShowAllPrivate={handleShowAllPrivate}
                onHideAll={handleHideAllCards}
                onTogglePlayerVisibility={handleTogglePlayerCardVisibility}
              />

              {/* Pause/Resume Button */}
              <Pressable
                onPress={() => {
                  isPaused ? resumeGame({ gameId }) : pauseGame({ gameId })
                }}
                style={({ pressed }) => [styles.button, isPaused && styles.activeButton, pressed && { opacity: 0.7 }]}
              >
                <Text style={[styles.buttonText, isPaused && styles.activeButtonText]}>
                  {isPaused ? 'Resume' : 'Pause'}
                </Text>
              </Pressable>
            </View>
          )}
        </View>
      </Draggable>

      {canAddBots && (
        <AddBotsModal
          visible={showAddBotsSheet}
          availableSeats={availableSeats}
          isAddingDuringGame={isAddingDuringGame}
          skillCounts={botSkillCounts}
          isSubmitting={isAddingBots}
          onClose={handleCloseAddBotsSheet}
          onConfirm={handleAddBots}
          onIncrement={handleIncrementBotSkill}
          onDecrement={handleDecrementBotSkill}
        />
      )}
    </>
  )
}

const styles = StyleSheet.create({
  draggableContainer: {
    position: 'absolute',
    top: 100,
    left: 10,
    zIndex: 1000
  },
  container: {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    padding: 12,
    borderRadius: 12,
    minWidth: 240,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 8,
    borderWidth: 1,
    borderColor: 'rgba(124, 77, 255, 0.3)'
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)'
  },
  title: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600'
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12
  },
  collapseIcon: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.7)'
  },
  closeIcon: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.7)',
    fontWeight: '600'
  },
  content: {
    paddingTop: 12,
    gap: 8
  },
  button: {
    padding: 10,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CREAM
  },
  activeButton: {
    backgroundColor: BLACK
  },
  buttonText: {
    color: BLACK,
    fontSize: 13,
    fontWeight: '600'
  },
  activeButtonText: {
    color: CREAM
  }
})
